import { ProgramFixedContentRow } from '../types/domain';
import { canWriteSchoolScopedDataCache } from './schoolDataCacheVersion';
import { buildLearningContentScopeVersion } from './learningContentCacheVersion';
import { deviceCacheStorage } from './deviceCacheStorage';

const PROGRAM_FIXED_CONTENT_CACHE_VERSION = 'v4';

type ProgramFixedContentCachePayload = {
  rows: ProgramFixedContentRow[];
  savedAt: number;
  contentVersion?: string;
};

function storageKey(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): string {
  return `cp.programFixedContentRows.${PROGRAM_FIXED_CONTENT_CACHE_VERSION}.${workspaceId}.${academicYear}.${termNumber}.${gradeId}`;
}

function normalizeRows(raw: unknown): ProgramFixedContentRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((row): row is ProgramFixedContentRow => !!row && typeof row === 'object');
}

export function loadProgramFixedContentRowsCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): { found: boolean; rows: ProgramFixedContentRow[]; contentVersion?: string } {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return { found: false, rows: [] };
  try {
    const raw = deviceCacheStorage.getItem(storageKey(workspaceId, academicYear, termNumber, gradeId));
    if (!raw) return { found: false, rows: [] };
    const parsed = JSON.parse(raw) as ProgramFixedContentCachePayload | ProgramFixedContentRow[];
    if (Array.isArray(parsed)) {
      const rows = normalizeRows(parsed);
      return { found: true, rows, contentVersion: buildLearningContentScopeVersion(rows) };
    }
    if (!parsed || typeof parsed !== 'object') return { found: false, rows: [] };
    const payload = parsed as ProgramFixedContentCachePayload;
    const rows = normalizeRows(payload.rows);
    return {
      found: true,
      rows,
      contentVersion:
        (payload.contentVersion ?? '').trim() || buildLearningContentScopeVersion(rows),
    };
  } catch {
    return { found: false, rows: [] };
  }
}

export function saveProgramFixedContentRowsCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  rows: ProgramFixedContentRow[],
  options?: { contentVersion?: string }
): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined' || !canWriteSchoolScopedDataCache(workspaceId)) return;
  try {
    const payload: ProgramFixedContentCachePayload = {
      rows,
      savedAt: Date.now(),
      contentVersion:
        (options?.contentVersion ?? '').trim() || buildLearningContentScopeVersion(rows),
    };
    deviceCacheStorage.setItem(storageKey(workspaceId, academicYear, termNumber, gradeId), JSON.stringify(payload));
  } catch {
    // ignore quota / private mode write errors
  }
}

/** 저장·삭제 후 DB와 불일치할 수 있으므로 다음 get 시 서버에서 다시 받게 함 */
export function clearProgramFixedContentRowsCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return;
  try {
    deviceCacheStorage.removeItem(storageKey(workspaceId, academicYear, termNumber, gradeId));
  } catch {
    // ignore
  }
}

/** 연간진도표·대시보드: 같은 학년도 1·2학기 고정행 LS를 합쳐 셀 키로 조회한다. */
export function mergeFixedContentRowsFromBothTerms(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): ProgramFixedContentRow[] {
  const primary = loadProgramFixedContentRowsCache(workspaceId, academicYear, termNumber, gradeId);
  const alternate = loadProgramFixedContentRowsCache(
    workspaceId,
    academicYear,
    termNumber === 1 ? 2 : 1,
    gradeId
  );
  const byCellKey = new Map<string, ProgramFixedContentRow>();
  const passthrough: ProgramFixedContentRow[] = [];
  const ingest = (rows: ProgramFixedContentRow[]) => {
    rows.forEach((row) => {
      const k = (row.source_annual_cell_key ?? '').trim();
      if (!k) {
        passthrough.push(row);
        return;
      }
      if (!byCellKey.has(k)) byCellKey.set(k, row);
    });
  };
  ingest(primary.rows);
  ingest(alternate.rows);
  return [...passthrough, ...Array.from(byCellKey.values())];
}

export function mergeProgramFixedContentRowsCacheRows(
  existingRows: ProgramFixedContentRow[],
  updatedRows: ProgramFixedContentRow[]
): ProgramFixedContentRow[] {
  if (updatedRows.length === 0) return existingRows;
  const updatedById = new Map<string, ProgramFixedContentRow>();
  updatedRows.forEach((row) => {
    if (row.id) updatedById.set(row.id, row);
  });
  const merged = existingRows.map((row) => updatedById.get(row.id) ?? row);
  const existingIdSet = new Set(existingRows.map((row) => row.id));
  updatedRows.forEach((row) => {
    if (!existingIdSet.has(row.id)) merged.push(row);
  });
  return merged;
}

